import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Header from "../components/Header";
import EventCard from "../components/EventCard";
import EventsCalendar from "../components/EventsCalendar";
import Footer from "../components/Footer";
import { EVENT_CATEGORIES } from "../data/events";
import { CalendarDays, List as ListIcon, ChevronLeft, ChevronRight } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function groupByDate(events) {
  const groups = {};
  events.forEach((e) => {
    (groups[e.date] = groups[e.date] || []).push(e);
  });
  return Object.keys(groups)
    .sort()
    .map((date) => ({ date, events: groups[date] }));
}

function dateLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
}

const PINNED_CITIES = ["delhi", "bangalore", "mumbai", "sf", "singapore", "dubai"];

function sortCities(cities) {
  return [...cities].sort((a, b) => {
    const ai = PINNED_CITIES.indexOf(a.slug);
    const bi = PINNED_CITIES.indexOf(b.slug);
    if (ai !== -1 && bi !== -1) return ai - bi;   // both pinned → keep pinned order
    if (ai !== -1) return -1;                       // only a is pinned → a first
    if (bi !== -1) return 1;                        // only b is pinned → b first
    return b.count - a.count;                       // neither pinned → sort by event count
  });
}

export default function Events({ theme, toggleTheme }) {
  const [cities, setCities] = useState([]);
  const [citySlug, setCitySlug] = useState("sf");
  const [defaultApplied, setDefaultApplied] = useState(false);
  const [view, setView] = useState("list");
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    api.get("/cities").then((res) => {
      const sorted = sortCities(res.data);
      setCities(sorted);
      if (!defaultApplied) {
        const withEvents = sorted.find((c) => c.count > 0);
        if (withEvents) setCitySlug(withEvents.slug);
        setDefaultApplied(true);
      }
    });
  }, [defaultApplied]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/events", { params: { city: citySlug } })
      .then((res) => setAllEvents(res.data))
      .finally(() => setLoading(false));
  }, [citySlug]);

  const city = cities.find((c) => c.slug === citySlug);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allEvents.filter((e) => {
      if (cat !== "all" && e.category !== cat) return false;
      if (!q) return true;
      return (
        e.title.toLowerCase().includes(q) ||
        (e.organizer || "").toLowerCase().includes(q) ||
        (e.venue || "").toLowerCase().includes(q)
      );
    });
  }, [allEvents, cat, query]);

  useEffect(() => {
    setPage(1);
  }, [citySlug, cat, query, view]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageEvents = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const grouped = useMemo(() => groupByDate(pageEvents), [pageEvents]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        query={query}
        setQuery={setQuery}
        theme={theme}
        toggleTheme={toggleTheme}
        searchPlaceholder="search events..."
      />

      <main className="mx-auto max-w-[1000px] overflow-x-hidden px-4 pb-24">
        {/* Hero */}
        <div className="py-6">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Founder &amp; startup events by city
          </h1>
          <p className="mt-1 text-[14px] text-muted-foreground">
            Every founder event in one place — conferences, demo days, dinners, and meetups.
          </p>
        </div>

        {/* City selector */}
        <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
          {cities.map((c) => {
            const active = c.slug === citySlug;
            return (
              <button
                key={c.slug}
                data-testid={`city-tab-${c.slug}`}
                onClick={() => setCitySlug(c.slug)}
                className={`group relative h-24 w-40 shrink-0 overflow-hidden rounded-lg border text-left transition-all ${
                  active ? "border-brand ring-2 ring-brand/40" : "border-border/60 hover:border-ring"
                }`}
              >
                <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-1.5 left-2 right-2">
                  <div className="text-[13px] font-semibold leading-tight text-white">{c.name}</div>
                  <div className="text-[11px] text-white/70">{c.count} events</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="mt-5 flex flex-col gap-3 border-b border-border/60 pb-4 sm:flex-row sm:flex-wrap sm:items-center">
          {/* View toggle + result count row */}
          <div className="flex items-center justify-between gap-3 sm:contents">
            <div className="flex items-center rounded-md border border-border bg-secondary/40 p-0.5">
              <button
                onClick={() => setView("list")}
                className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  view === "list" ? "bg-brand text-brand-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ListIcon className="h-3.5 w-3.5" /> List
              </button>
              <button
                onClick={() => setView("calendar")}
                className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  view === "calendar" ? "bg-brand text-brand-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <CalendarDays className="h-3.5 w-3.5" /> Calendar
              </button>
            </div>

            <span className="text-[13px] text-muted-foreground sm:ml-auto sm:order-last">
              {filtered.length} events in {city?.name}
            </span>
          </div>

          {/* Category chips — horizontally scrollable on mobile */}
          <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5 sm:mx-0 sm:flex-wrap sm:px-0 sm:pb-0">
            <CatChip label="All" active={cat === "all"} onClick={() => setCat("all")} />
            {EVENT_CATEGORIES.map((c) => (
              <CatChip key={c} label={c} active={cat === c} onClick={() => setCat(c)} />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="mt-5">
          {loading ? (
            <div className="py-24 text-center text-muted-foreground">Loading events...</div>
          ) : view === "calendar" ? (
            <EventsCalendar events={filtered} />
          ) : grouped.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground">No events match your filters.</div>
          ) : (
            <div className="space-y-7">
              {grouped.map((g) => (
                <section key={g.date}>
                  <div className="mb-3 flex items-baseline gap-2">
                    <h2 className="text-[15px] font-semibold text-foreground">{dateLabel(g.date)}</h2>
                    <span className="text-[12px] text-muted-foreground">{g.events.length}</span>
                  </div>
                  <div className="grid gap-2.5">
                    {g.events.map((e) => (
                      <EventCard key={e.id} event={e} cityImage={city?.image} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        {view === "list" && !loading && totalPages > 1 && (
          <EventsPagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}

      </main>

      <Footer />
    </div>
  );
}

function CatChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors ${
        active
          ? "border-brand bg-brand/10 text-brand"
          : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function EventsPagination({ page, totalPages, onPageChange }) {
  const pages = useMemo(() => {
    const window = 2;
    const nums = new Set([1, totalPages]);
    for (let i = page - window; i <= page + window; i++) {
      if (i >= 1 && i <= totalPages) nums.add(i);
    }
    return Array.from(nums).sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <nav
      data-testid="events-pagination"
      aria-label="pagination"
      className="mt-8 flex items-center justify-center gap-1.5"
    >
      <button
        data-testid="pagination-prev"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-secondary/40 text-foreground transition-colors hover:bg-secondary disabled:opacity-40 disabled:hover:bg-secondary/40"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {pages.map((n, i) => {
        const prev = pages[i - 1];
        const showEllipsis = prev !== undefined && n - prev > 1;
        return (
          <React.Fragment key={n}>
            {showEllipsis && <span className="px-1 text-muted-foreground">…</span>}
            <button
              data-testid={`pagination-page-${n}`}
              onClick={() => onPageChange(n)}
              className={`flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-[13px] font-medium transition-colors ${
                n === page
                  ? "border-brand bg-brand text-brand-foreground"
                  : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
              }`}
            >
              {n}
            </button>
          </React.Fragment>
        );
      })}

      <button
        data-testid="pagination-next"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-secondary/40 text-foreground transition-colors hover:bg-secondary disabled:opacity-40 disabled:hover:bg-secondary/40"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );
}
